import type { ExperienceManifest, FileTypeDefinition } from "./experience";
import type { StructuredSchema } from "./structured-schema";
import { fieldDefinitionSchema } from "./structured-schema";
import type { FieldDefinition } from "./templates";
import type { JsonObject, WorldDocument } from "./types";

export interface ReleaseDependency {
  id: string;
  experienceId: string;
  kind: "extension" | "layout" | "ruleset" | "tool_contract" | "map";
  version: number;
  content: JsonObject;
}
export interface ReleaseReferenceIssue { documentId: string; path: string[]; targetId: string; reason: string }

const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);
const normalized = (value: string) => value.replaceAll("-", "_");
const referenceSchema = (field: FieldDefinition): StructuredSchema => ({
  ...fieldDefinitionSchema(field),
  ...(field.type === "reference" && !field.schema?.reference && !field.schema?.referencedFileTypeSlug ? { reference: { targets: (field.referenceKinds ?? []).map(fileTypeSlug => ({ fileTypeSlug })) } } : {}),
  ...(field.item ? { items: referenceSchema(field.item) } : {}),
  ...(field.properties ? { properties: Object.fromEntries(field.properties.map(child => [child.id, referenceSchema(child)])) } : {}),
});

/** Read only declared reference fields: arbitrary narrative strings are not IDs. */
export function releaseReferenceIssues(documents: readonly WorldDocument[], types: readonly FileTypeDefinition[]): ReleaseReferenceIssue[] {
  const live = new Map(documents.filter(doc => !doc.deletedAt).map(doc => [doc.id, doc]));
  const issues: ReleaseReferenceIssue[] = [];
  for (const doc of live.values()) {
    const check = (value: unknown, path: string[], targetTypes: string[] = []) => {
      if (value === null || value === undefined || value === "") return;
      if (Array.isArray(value)) { value.forEach((entry, index) => check(entry, [...path, String(index)], targetTypes)); return; }
      const id = typeof value === "string" ? value : object(value) ? value.id ?? value.slug : undefined;
      if (typeof id !== "string" || !id.trim()) { issues.push({ documentId: doc.id, path, targetId: "", reason: "Invalid reference identity" }); return; }
      const target = live.get(id);
      if (!target) issues.push({ documentId: doc.id, path, targetId: id, reason: "Reference target is missing or retired" });
      else if (targetTypes.length) {
        const definition = types.find(type => type.id === target.fileTypeId);
        if (!targetTypes.some(type => [target.fileTypeId, target.kind, definition?.designation].some(actual => actual && normalized(actual) === normalized(type)))) {
          issues.push({ documentId: doc.id, path, targetId: id, reason: "Reference target has the wrong file type" });
        }
      }
    };
    doc.references.forEach((ref, index) => check(ref.targetId, ["references", String(index), "targetId"]));
    const walk = (value: unknown, schema: StructuredSchema, path: string[]) => {
      if (schema.reference || schema.referencedFileTypeSlug) {
        const targets = schema.reference?.targets?.flatMap(target => target.fileTypeSlug ? [target.fileTypeSlug] : []) ?? [];
        if (schema.referencedFileTypeSlug) targets.push(schema.referencedFileTypeSlug);
        check(value, path, targets);
      } else if (Array.isArray(value) && schema.items) value.forEach((entry, index) => walk(entry, schema.items!, [...path, String(index)]));
      else if (object(value)) for (const [key, child] of Object.entries(schema.properties ?? {})) walk(value[key], child, [...path, key]);
    };
    const definition = types.find(type => type.id === doc.fileTypeId) ?? types.find(type => type.designation === doc.kind);
    if (definition) {
      const schema = object(definition.sourceSchema) ? definition.sourceSchema as StructuredSchema : { properties: Object.fromEntries(definition.fields.map(field => [field.id, referenceSchema(field)])) };
      walk(doc.fields, schema, ["fields"]);
    }
  }
  return issues;
}

export function validateReleaseDependencies(manifest: ExperienceManifest, documents: readonly WorldDocument[], types: readonly FileTypeDefinition[], dependencies: readonly ReleaseDependency[]): void {
  const ids = new Set<string>();
  for (const dependency of dependencies) {
    const key = `${dependency.kind}:${dependency.id}`;
    if (!dependency.id.trim() || ids.has(key) || dependency.experienceId !== manifest.id || !Number.isInteger(dependency.version) || dependency.version < 1) throw new Error("Invalid release dependency identity, version or scope");
    ids.add(key);
  }
  const resource = (kind: ReleaseDependency["kind"], id: string) => ids.has(`${kind}:${id}`);
  const live = new Map(documents.filter(doc => !doc.deletedAt).map(doc => [doc.id, doc]));
  const documentKind = (id: string, kinds: string[]) => {
    const doc = live.get(id);
    const type = types.find(type => type.id === doc?.fileTypeId);
    return Boolean(doc && kinds.some(kind => normalized(type?.designation ?? doc.kind) === kind));
  };
  const requireIds = (values: readonly string[], label: string, exists: (id: string) => boolean) => {
    if (new Set(values).size !== values.length) throw new Error(`Duplicate ${label} dependency`);
    for (const id of values) if (!exists(id)) throw new Error(`Missing or incompatible ${label} dependency: ${id}`);
  };
  requireIds(manifest.gameStartIds, "game start", id => documentKind(id, ["game_start"]));
  requireIds(manifest.instructionIds, "instruction", id => documentKind(id, ["gm_instructions", "optional_gm_instructions", "instruction"]));
  requireIds(manifest.mapIds, "map", id => documentKind(id, ["map", "scene_map"]) || resource("map", id));
  requireIds(manifest.rulesetIds, "ruleset", id => documentKind(id, ["rule", "ruleset"]) || resource("ruleset", id));
  requireIds(manifest.layoutIds, "layout", id => resource("layout", id));
  requireIds(manifest.extensionIds, "extension", id => resource("extension", id));
  requireIds(manifest.toolContractIds, "tool contract", id => resource("tool_contract", id));
  for (const type of types) {
    requireIds(type.extensionIds, "file type extension", id => manifest.extensionIds.includes(id) && resource("extension", id));
    requireIds(type.featuredFileIds ?? [], "featured file", id => live.get(id)?.fileTypeId === type.id);
    const checkSchema = (schema: StructuredSchema) => {
      const targets = schema.reference?.targets?.flatMap(target => target.fileTypeSlug ? [target.fileTypeSlug] : []) ?? [];
      if (schema.referencedFileTypeSlug) targets.push(schema.referencedFileTypeSlug);
      for (const target of targets) if (!types.some(candidate => candidate.id === target || normalized(candidate.designation) === normalized(target))) throw new Error(`Missing referenced file type dependency: ${target}`);
      if (schema.items) checkSchema(schema.items);
      Object.values(schema.properties ?? {}).forEach(checkSchema);
    };
    if (object(type.sourceSchema)) checkSchema(type.sourceSchema as StructuredSchema);
    type.fields.forEach(field => checkSchema(referenceSchema(field)));
  }
}
